<template>
  <div>
    <ts-page-title
      :title="$t('serialProfileReport.pageTitle')"
      :breadcrumb="[
        { text: $t('home'), href: '/' },
        {
          text: $t('serialProfileReport.pageTitle'),
          active: true
        }
      ]"
    />
    <ts-panel>
      <ts-panel-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-lg-3">
              <div class="card">
                <div
                  class="card-body border-0 ts-bg-gray-900"
                  style="border-top-left-radius: 8px; border-top-right-radius: 8px;"
                >
                  <div class="ts-text-gray-500">
                    <b>{{ $t('serialProfileReport.filterCriteria') }}</b>
                  </div>
                </div>
                <div class="widget-list rounded-bottom">
                  <div class="mb-3 mt-3 ts-px-3">
                    <div>
                      <label>{{ $t('serialProfileReport.branch') }}</label>
                      <ts-branch-filter
                        @filter="value => (model.branch_id = value)"
                        :isPlaceholder="true"
                        style="width: 100%"
                      />
                      <div class="validate" v-if="errors.has('branch_id')">
                        {{ errors.first('branch_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="mb-3 mt-3 ts-px-3">
                    <div>
                      <label>{{ $t('serialProfileReport.status') }}</label>
                      <a-select
                        v-model="sn_status_id"
                        :maxTagCount="1"
                        mode="multiple"
                        :placeholder="$t('all')"
                        class="ts-w-full"
                      >
                        <a-select-option
                          v-for="(s, index) in serialStatus"
                          :key="index"
                          :value="s.sn_status_id"
                        >
                          {{ s.sn_status }}
                        </a-select-option>
                      </a-select>
                    </div>
                  </div>
                  <div
                    class="ts-flex ts-flex-wrap sm:ts-flex-nowrap ts-justify-end ts-gap-2 ts-px-3"
                  >
                    <ts-button
                      outline
                      color="success"
                      @click.prevent="exportExcel"
                      :waiting="exporting"
                      class="ts-w-full sm:ts-w-auto"
                    >
                      <i class="far fa-file-excel" v-if="!exporting"></i>
                      {{ $t('exportExcel') }}
                    </ts-button>
                    <ts-button
                      color="primary"
                      @click.prevent="fetchData"
                      :waiting="viewing"
                      class="ts-w-full sm:ts-w-auto"
                    >
                      <i class="far fa-eye" v-if="!viewing"></i>
                      {{ $t('view') }}
                    </ts-button>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-9">
              <div v-if="!showPreview">
                <div class="ts-mb-3 ts-flex ts-justify-end">
                  <ts-input
                    v-model="search"
                    v-tooltip="$t('serialProfileReport.search')"
                    :placeholder="$t('search')"
                    style="width: 250px"
                    class="inline-block"
                  />
                </div>
                <div class="overflow-auto" style="max-height: 75vh">
                  <ts-table
                    :columns="columns"
                    :records="resources"
                    :loading="viewing"
                  >
                    <template v-slot="{ record, index }">
                      <td class="text-center">{{ record.branch_name_en }}</td>
                      <td class="text-center">{{ record.serial_no }}</td>
                      <td class="text-center">
                        {{
                          (record.product &&
                            record.product.group &&
                            record.product.group.pro_group_name_en) ||
                            ''
                        }}
                      </td>
                      <td class="text-center">
                        {{
                          record.product ? record.product.product_name_en : ''
                        }}
                      </td>
                      <td class="text-center">
                        {{
                          record.stock_in_number ? record.stock_in_number : ''
                        }}
                      </td>
                      <td class="text-center">
                        {{ record.stock_in_date ? record.stock_in_date : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.color ? record.color : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.imei ? record.imei : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.weight ? record.weight : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.cost ? record.cost : '' }}
                      </td>

                      <td class="text-center">
                        {{ record.repair_cost ? record.repair_cost : '' }}
                      </td>

                      <td class="text-center">
                        {{ calculateTotalCost(record) }}
                      </td>
                      <td class="text-center">
                        {{
                          record.location_name_en ? record.location_name_en : ''
                        }}
                      </td>
                      <td class="text-center">
                        {{
                          record.location_name_en ? record.location_name_en : ''
                        }}
                      </td>
                      <td>{{ record.sn_status ? record.sn_status : '' }}</td>
                      <td>{{ record.invoice_no ? record.invoice_no : '' }}</td>
                      <td>
                        {{ record.stock_out_date ? record.stock_out_date : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.qc_remarks ? record.qc_remarks : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.engine_number ? record.engine_number : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.mc_number ? record.mc_number : '' }}
                      </td>
                      <td class="text-center">
                        {{ record.plate_number ? record.plate_number : '' }}
                      </td>
                      <td class="text-center">
                        {{
                          (record.stock_in &&
                            record.stock_in.user &&
                            record.stock_in.user.name) ||
                            ''
                        }}
                      </td>
                      <td class="text-center">
                        {{
                          (record.stock_in &&
                            record.stock_in.supplier &&
                            record.stock_in.supplier.vendor_name) ||
                            ''
                        }}
                      </td>
                      <td class="text-center">
                        {{ record.stock_in ? record.stock_in.invoice_no : '' }}
                      </td>
                      <td class="text-center">
                        Quantity
                      </td>
                      <td class="text-center">
                        1
                      </td>
                    </template>
                  </ts-table>
                </div>
                <div class="ts-flex ts-items-center ts-justify-end ts-pt-4">
                  <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                  ></ts-i-pagination>
                </div>
              </div>

              <!-- PDF preview -->
              <div v-else>
                <ts-card>
                  <ts-card-wrapper>
                    <form>
                      <fieldset>
                        <legend>{{ $t('serialProfileReport.preview') }}</legend>
                        <ts-preview-pdf v-model="waiting" :src="src" />
                      </fieldset>
                    </form>
                  </ts-card-wrapper>
                </ts-card>
              </div>
            </div>
          </div>
        </ts-loading-banner>
      </ts-panel-wrapper>
    </ts-panel>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { debounce } from 'lodash'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'serialProfile',
  data() {
    return {
      loading: false,
      exporting: false,
      viewing: false,
      loadingPreData: false,
      waiting: false,
      errors: new Errors(),
      src: null,
      showPreview: false,
      sn_status_id: [1],
      model: {
        date_range: [
          moment()
            .startOf('M')
            .format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ],
        branch_id: []
      }
    }
  },
  computed: {
    ...mapState('report/serialProfile', [
      'resources',
      'pagination',
      'serialStatus'
    ]),
    search: {
      get() {
        return this.$store.state.report.serialProfile.search
      },
      set(newValue) {
        this.$store.commit('report/serialProfile/SET_SEARCH', newValue)
        this.$store.commit('report/serialProfile/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          name: this.$t('serialProfileReport.branch'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.serial'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.group'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.productName'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.stockInNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.stockInDate'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.color'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.imeiCode'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.weight'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.cost'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.additionalCost'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.totalCost'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.originalWarehouse'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.wareHouse'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.status'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.invoiceNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.invoiceDate'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.remark'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.engineNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.mcNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.plateNumber'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.stockInBy'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.vendor'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.ref'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.uom'),
          style: 'text-align:center'
        },
        {
          name: this.$t('serialProfileReport.qty'),
          style: 'text-align:center'
        }
      ]
    }
  },
  methods: {
    ...mapActions('report/serialProfile', ['fetchSerialStatus']),
    fetchData(attributes) {
      this.errors = new Errors()
      this.viewing = true
      this.showPreview = false
      this.$store
        .dispatch('report/serialProfile/fetch', {
          date_range: this.model.date_range,
          branch_id: this.model.branch_id,
          sn_status_id: this.sn_status_id,
          ...attributes
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.viewing = false))
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()
      this.$store
        .dispatch('report/inventory/serialProfileExcel', {
          ...this.model,
          sn_status_id: this.sn_status_id,
          'filter[search]': this.search
        })
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    formatCurrencyInteger(value) {
      if (value == null || isNaN(value)) return '0'
      return Math.round(value).toLocaleString() // rounds and adds commas
    },
    calculateTotalCost(record) {
    const cost = parseFloat(record.cost || 0)
    const repairCost = parseFloat(record.repair_cost || 0)
    return cost + repairCost
  },
    notice(not) {
      this.$Notice[not.type]({
        title: 'Serial Profile',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchSerialStatus()
    })
  }
}
</script>
<style scoped>
table th,
table td {
  white-space: nowrap;
}
</style>
